import React from 'react';

interface LoadingSpinnerProps {
  message?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ message = 'Loading...' }) => {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-surface overflow-hidden">
      {/* Ambient gradient blobs */}
      <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-[#007bba]/5 blur-[100px] rounded-full pointer-events-none" />

      {/* Centered content */}
      <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-md px-4">
        {/* Logo & branding */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-primary">
            <span
              className="material-symbols-outlined text-surface text-4xl"
              style={{ fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
            >
              architecture
            </span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <h1 className="font-black text-3xl tracking-tighter text-on-surface">
              Role Bot
            </h1>
            <span className="text-[10px] uppercase tracking-[0.2em] text-on-surface-variant">
              Role Management System
            </span>
          </div>
        </div>

        {/* Loading card */}
        <div className="w-full bg-surface-container-low rounded-xl p-8 space-y-8 shadow-2xl">
          {/* Status header */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-[0.15em] text-on-surface-variant">
                System Status
              </span>
              <h2 className="text-lg font-semibold text-on-surface">{message}</h2>
            </div>
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>

          {/* Process indicators */}
          <div className="space-y-4">
            {/* Complete */}
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-success text-xl">check_circle</span>
              <span className="text-sm text-on-surface-variant">Secure Handshake Established</span>
            </div>

            {/* Active */}
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-xl animate-pulse">
                key
              </span>
              <div className="flex-1 space-y-2">
                <span className="text-sm text-on-surface">{message}</span>
                <div className="h-1 w-full rounded-full bg-surface-container-highest overflow-hidden">
                  <div className="h-full w-[45%] rounded-full bg-primary transition-all duration-500" />
                </div>
              </div>
            </div>

            {/* Pending skeleton items */}
            <div className="flex items-center gap-3 opacity-40">
              <div className="h-5 w-5 rounded skeleton-gradient" />
              <div className="h-4 w-48 rounded skeleton-gradient" />
            </div>
            <div className="flex items-center gap-3 opacity-40">
              <div className="h-5 w-5 rounded skeleton-gradient" />
              <div className="h-4 w-36 rounded skeleton-gradient" />
            </div>
          </div>

          {/* Technical feed */}
          <div className="bg-surface-container-lowest border-l-2 border-primary/30 p-4 rounded-lg font-mono text-[11px] text-on-surface-variant space-y-1.5">
            <p>
              <span className="text-primary/60">[init]</span> Resolving activity context...
            </p>
            <p>
              <span className="text-success/60">[auth]</span> OAuth2 handshake complete
            </p>
            <p className="animate-pulse">
              <span className="text-primary/60">[sync]</span> Fetching guild manifest...
            </p>
          </div>

          {/* Disabled button */}
          <button
            disabled
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-surface-container-highest px-4 py-3 text-sm font-medium text-on-surface-variant cursor-not-allowed opacity-60"
          >
            <div className="h-4 w-4 rounded-full border-2 border-on-surface-variant border-t-transparent animate-spin" />
            Verifying Identity
          </button>
        </div>
      </div>

      {/* Bottom status pill */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20">
        <div className="flex items-center gap-2 bg-surface-container-high/80 backdrop-blur-md rounded-full px-4 py-2 text-[11px] text-on-surface-variant">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          Establishing secure connection
        </div>
      </div>
    </div>
  );
};
